// Vista 360 de un usuario — la pantalla donde soporte trabaja una llamada.
//
// Layout: columna principal con una tarjeta por grupo de datos, y a la derecha
// (28% del ancho) el historial de acciones administrativas, fijo mientras se
// baja por el resto. Ese panel va aparte porque responde otra pregunta: no
// "¿qué le pasa a esta cuenta?" sino "¿qué le hicimos nosotros?", y suele ser
// lo primero que explica un comportamiento raro.
//
// El orden de arriba hacia abajo sigue el de las preguntas reales: primero
// quién es y si puede entrar, después qué contrató y si pagó, después quién
// está usando la cuenta.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeft,
  Ban,
  Check,
  ChevronDown,
  CircleDollarSign,
  Clock,
  CreditCard,
  FileText,
  Gift,
  Globe,
  History,
  ListChecks,
  Hourglass,
  IdCard,
  Laptop,
  Lock,
  LockOpen,
  LogOut,
  Mail,
  MailSearch,
  MapPin,
  Monitor,
  RefreshCw,
  ScrollText,
  Smartphone,
  Tablet,
  Ticket,
  Tv,
  User as UserIcon,
  X,
} from 'lucide-react';
import {
  fetchUserDetail,
  adminLogoutAll,
  adminBlockUser,
  adminUnblockUser,
  adminGrantHallPass,
  hallPassLabel,
  HALL_PASS_OPTIONS,
  adminReconcileSubscription,
  adminDeleteUserDevice,
  AdminApiError,
  type AdminDeviceSummary,
  type AdminUserInvoice,
  type AdminUserPayment,
  type AdminUserSubscription,
  type CheckoutAttempt,
  type HallPassResult,
  type ReconcileResult,
  type UserDetailResponse,
  type VerificationGraceState,
} from '@/lib/api';
import {
  absoluteTime,
  countdown,
  daysUntil,
  money,
  relativeSeconds,
  relativeTime,
  shortDate,
} from '@/lib/format';
import { graceWorthShowing } from '@/lib/grace';
import { chargeSituation, declineLabel, type ChargeSituation } from '@/lib/charge-state';
import { SUB_LABEL, SUB_TONE } from '@/lib/subscription-status';
import {
  ATTEMPT_VIEW,
  OUTCOME_VIEW,
  attemptFor,
  duplicateAttempts,
  isLinePaymentId,
  lineStatusLabel,
  normalizeLinePaymentId,
  outcomeDetail,
  reconcileSummary,
  shortPaymentId,
  type ReconcileTone,
} from '@/lib/reconcile';
import ChargeRetryButton from '@/components/ChargeRetry';
import MarkRefundedButton from '@/components/MarkRefunded';
import { useSession } from '@/hooks/useSession';
import { canWrite } from '@/lib/access';
import ConfirmDialog from '@/components/ConfirmDialog';
import EmailReportModal from '@/components/EmailReportModal';
import {
  CopyButton,
  DataCard,
  EmptyNote,
  ExpandableRow,
  Field,
  Pill,
  StatCard,
} from '@/components/DataCard';
import { fieldCls } from '@/lib/form-styles';

// ---------------------------------------------------------------------------
// Badges
// ---------------------------------------------------------------------------

const PAYMENT_TONE = {
  succeeded: 'success',
  pending: 'warning',
  failed: 'error',
  refunded: 'info',
} as const;

const PAYMENT_LABEL = {
  succeeded: 'acreditado',
  pending: 'pendiente',
  failed: 'rechazado',
  refunded: 'devuelto',
} as const;

const INVOICE_TONE = {
  authorized: 'success',
  pending: 'neutral',
  sending: 'info',
  submitted: 'info',
  rejected: 'error',
  error: 'error',
} as const;

const DEVICE_ICON = {
  smarttv: Tv,
  mobile: Smartphone,
  tablet: Tablet,
  web: Monitor,
  other: Laptop,
} as const;

const DEVICE_LABEL = {
  smarttv: 'Smart TV',
  mobile: 'Celular',
  tablet: 'Tablet',
  web: 'Web',
  other: 'Otro',
} as const;

// ---------------------------------------------------------------------------
// Ventana de gracia de verificación (pastilla de visibilidad condicional)
// ---------------------------------------------------------------------------

/**
 * Segundos que faltan, descontados en vivo.
 *
 * El plazo lo midió Postgres — el mismo reloj que decide el gate del login — y
 * acá se ancla contra el reloj local en el instante en que llegó. Lo que aporta
 * el navegador es "cuánto pasó desde que lo recibí", que es confiable; nunca
 * "qué hora es", que puede estar corrido horas y mostraría una cuenta regresiva
 * que no coincide con la realidad del backend.
 *
 * El intervalo solo corre mientras queda tiempo: una ventana ya vencida no
 * necesita repintarse una vez por segundo.
 */
function useLiveSeconds(serverSeconds: number): number {
  const [anchor, setAnchor] = useState(() => ({ at: Date.now(), secs: serverSeconds }));
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    setAnchor({ at: Date.now(), secs: serverSeconds });
    setNow(Date.now());
  }, [serverSeconds]);

  const remaining = anchor.secs - Math.floor((now - anchor.at) / 1000);
  const running = remaining > 0;

  useEffect(() => {
    if (!running) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [running]);

  return remaining;
}

function GraceStatCard({ grace }: { grace: VerificationGraceState }): JSX.Element {
  const remaining = useLiveSeconds(grace.seconds_remaining ?? 0);
  const live = remaining > 0;
  const until = absoluteTime(grace.grace_until);

  return (
    <StatCard
      icon={Hourglass}
      label="Gracia de verificación"
      value={live ? <span className="tabular-nums">{countdown(remaining)}</span> : 'concluida'}
      detail={
        <span title={`${grace.partner_id} · suscripción ${grace.subscription_status} · hasta ${until}`}>
          {live
            ? grace.effective
              ? `hasta ${until}`
              : 'sin efecto: la suscripción no está activa'
            : `concluyó ${relativeSeconds(remaining)}`}
        </span>
      }
      // Vigente = verde, concluida = rojo. La excepción es la ventana escrita
      // sobre una sub que no está viva: existe en la tabla pero el gate del
      // login no la mira, así que pintarla de verde diría que el usuario puede
      // entrar cuando no puede.
      tone={live ? (grace.effective ? 'success' : 'warning') : 'error'}
    />
  );
}

function accountStatusPill(status: 'active' | 'blocked' | 'deactivated'): JSX.Element {
  if (status === 'active') return <Pill tone="success">activa</Pill>;
  if (status === 'blocked') return <Pill tone="error">bloqueada</Pill>;
  return <Pill tone="neutral">dada de baja</Pill>;
}

// ---------------------------------------------------------------------------

export default function UserDetail(): JSX.Element {
  const { userId = '' } = useParams<{ userId: string }>();

  const q = useQuery({
    queryKey: ['admin-user-detail', userId],
    queryFn: () => fetchUserDetail(userId),
    enabled: !!userId,
  });

  if (q.isLoading) {
    return <p className="text-sm text-ink-muted">Cargando usuario…</p>;
  }
  if (q.isError || !q.data) {
    return (
      <div
        role="alert"
        className="rounded-lg border border-status-error/30 bg-status-error/10 p-3 text-sm text-status-error"
      >
        No pudimos cargar este usuario.{' '}
        <Link to="/users" className="underline">
          Volver
        </Link>
      </div>
    );
  }

  return <UserDetailBody data={q.data} userId={userId} />;
}

function UserDetailBody({
  data,
  userId,
}: {
  data: UserDetailResponse;
  userId: string;
}): JSX.Element {
  const {
    user,
    subscriptions,
    payments,
    invoices,
    coupons,
    devices,
    tos_acceptances,
    recent_audit,
    verification_grace,
  } = data;
  // Campo nuevo y opcional: un backend anterior no lo manda.
  const checkoutAttempts = data.checkout_attempts ?? [];

  // Pagos y facturas se agrupan por suscripción una sola vez: cada fila
  // desplegable los toma de acá en vez de recorrer el arreglo entero.
  const paymentsBySub = useMemo(() => {
    const map = new Map<string, AdminUserPayment[]>();
    for (const p of payments) {
      const list = map.get(p.subscription_id) ?? [];
      list.push(p);
      map.set(p.subscription_id, list);
    }
    return map;
  }, [payments]);

  const invoiceByPayment = useMemo(() => {
    const map = new Map<string, AdminUserInvoice>();
    for (const i of invoices) map.set(i.payment_id, i);
    return map;
  }, [invoices]);

  const liveSub = subscriptions.find((s) =>
    ['active', 'trialing', 'grace_period'].includes(s.status),
  );
  // Alta iniciada sin pago acreditado: no da acceso, pero hay que verla.
  const pendingSub = liveSub ? undefined : subscriptions.find((s) => s.status === 'pending');
  const totalPaid = payments
    .filter((p) => p.status === 'succeeded')
    .reduce((sum, p) => sum + p.amount, 0);
  const paidCount = payments.filter((p) => p.status === 'succeeded').length;
  const failedPayments = payments.filter((p) => p.status === 'failed').length;
  const unresolvedInvoices = invoices.filter(
    (i) => i.status !== 'authorized' && i.status !== 'rejected',
  ).length;

  const showGrace = graceWorthShowing(verification_grace);

  // El primer cobro pendiente, para el aviso de arriba de la ficha.
  const pendingCharge =
    subscriptions
      .map((s) => subChargeSituation(s, paymentsBySub.get(s.subscription_id) ?? []))
      .find((x) => x !== null) ?? null;

  const fullName =
    [user.first_name, user.last_name].filter(Boolean).join(' ') ||
    user.display_name ||
    'Usuario sin nombre';

  return (
    <div className="space-y-4">
      {/* ---------------- Cabecera ---------------- */}
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 data-help-page="user.page" data-help="user.page" className="text-2xl font-semibold truncate">{fullName}</h1>
            {accountStatusPill(user.status)}
            {user.email_verified ? (
              <Pill tone="success" title="La cuenta de email fue validada">
                <Mail size={11} aria-hidden="true" />
                verificado
              </Pill>
            ) : (
              <Pill tone="warning" title="El usuario todavía no validó su email">
                <AlertTriangle size={11} aria-hidden="true" />
                email sin verificar
              </Pill>
            )}
          </div>
          <div className="flex items-center gap-1 text-sm text-ink-muted min-w-0">
            <span className="truncate">{user.email ?? 'sin email'}</span>
            {user.email ? <CopyButton value={user.email} label="email" /> : null}
          </div>
        </div>
        <Link
          to="/users"
          className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink dark:hover:text-ink-inverted hover:underline"
        >
          <ArrowLeft size={14} aria-hidden="true" />
          Volver a búsqueda
        </Link>
      </header>

      {user.status === 'blocked' ? (
        <div
          role="alert"
          className="rounded-lg border border-status-error/30 bg-status-error/10 p-3 text-sm text-status-error"
        >
          <strong>Cuenta bloqueada</strong>
          {user.blocked_at ? ` desde el ${absoluteTime(user.blocked_at)}` : ''}.
          {user.blocked_reason ? ` Motivo: ${user.blocked_reason}` : ' Sin motivo registrado.'}
        </div>
      ) : null}

      {pendingCharge ? (
        <div
          role="alert"
          className={`rounded-lg border p-3 text-sm flex flex-wrap items-center gap-x-3 gap-y-1 ${
            pendingCharge.tone === 'error'
              ? 'border-status-error/30 bg-status-error/10 text-status-error'
              : 'border-status-warning/30 bg-status-warning/10 text-status-warning'
          }`}
        >
          <strong>Cobro pendiente:</strong>
          <span>{pendingCharge.title}.</span>
          <a href="#suscripciones" className="underline underline-offset-2">
            Ver la suscripción y reintentar
          </a>
        </div>
      ) : null}

      {/* ---------------- Resumen a ojo de pájaro ----------------
          Cinco pastillas fijas + la de la gracia, que aparece solo cuando hay
          una ventana vigente o recién concluida. Va primera porque cuando
          existe es lo más urgente de la pantalla: es un plazo corriendo. */}
      <div
        data-help="user.summary"
        className={`grid gap-3 grid-cols-2 ${
          showGrace ? 'lg:grid-cols-6' : 'lg:grid-cols-5'
        }`}
      >
        {showGrace ? <GraceStatCard grace={verification_grace} /> : null}
        <StatCard
          icon={CreditCard}
          label="Suscripción"
          value={
            liveSub
              ? (liveSub.plan_name ?? liveSub.plan_code ?? 'plan')
              : pendingSub
                ? SUB_LABEL.pending
                : 'sin suscripción'
          }
          detail={
            liveSub?.current_period_end
              ? `vence ${relativeTime(liveSub.current_period_end)}`
              : pendingSub
                ? `intentó contratar ${pendingSub.plan_name ?? pendingSub.plan_code ?? 'un plan'}`
                : 'nunca contrató'
          }
          tone={
            liveSub
              ? liveSub.status === 'grace_period'
                ? 'warning'
                : 'success'
              : pendingSub
                ? 'warning'
                : 'neutral'
          }
        />
        <StatCard
          icon={CircleDollarSign}
          label="Total pagado"
          value={money(totalPaid, payments[0]?.currency ?? 'ARS')}
          detail={`${paidCount} cobro(s) acreditado(s)`}
        />
        <StatCard
          icon={X}
          label="Pagos rechazados"
          value={failedPayments}
          detail={failedPayments > 0 ? 'revisar el detalle' : 'ninguno'}
          tone={failedPayments > 0 ? 'error' : 'neutral'}
        />
        <StatCard
          icon={Monitor}
          label="Dispositivos"
          value={devices.length}
          detail={devices.length > 0 ? `visto ${relativeTime(devices[0]!.last_seen_at)}` : 'ninguno'}
        />
        <StatCard
          icon={FileText}
          label="Facturas"
          value={invoices.length}
          detail={unresolvedInvoices > 0 ? `${unresolvedInvoices} sin cerrar` : 'todas emitidas'}
          tone={unresolvedInvoices > 0 ? 'warning' : 'neutral'}
        />
      </div>

      <UserAdminActions user={user} userId={userId} />

      {/* ---------------- Cuerpo: principal + panel derecho ---------------- */}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,72fr)_minmax(0,28fr)] items-start">
        <div className="space-y-4 min-w-0">
          <div data-help="user.identity">
            <AccountCard user={user} partnerIds={data.partner_ids} />
          </div>

          <div id="suscripciones" className="scroll-mt-4" />
          <DataCard
            icon={CreditCard}
            title="Suscripciones"
            count={subscriptions.length}
            helpId="user.subscription"
          >
            {subscriptions.length === 0 ? (
              <EmptyNote>Este usuario nunca contrató un plan.</EmptyNote>
            ) : (
              subscriptions.map((s) => (
                <SubscriptionRow
                  key={s.subscription_id}
                  sub={s}
                  payments={paymentsBySub.get(s.subscription_id) ?? []}
                  invoiceByPayment={invoiceByPayment}
                  defaultOpen={s.subscription_id === liveSub?.subscription_id}
                  customer={fullName || null}
                />
              ))
            )}
          </DataCard>

          {checkoutAttempts.length > 0 ? (
            <AttemptsCard
              attempts={checkoutAttempts}
              userId={userId}
              customer={fullName || null}
            />
          ) : null}

          <div data-help="user.devices">
            <DevicesCard devices={devices} userId={userId} />
          </div>

          {coupons.length > 0 ? (
            <DataCard
              icon={Gift}
              title={
                coupons.some((c) => c.kind === 'hall_pass')
                  ? 'Cupones y Hall Pass'
                  : 'Cupones canjeados'
              }
              count={coupons.length}
              helpId="user.coupons"
            >
              <ul className="divide-y divide-surface-border dark:divide-surface-dark-border">
                {coupons.map((c) => (
                  <li key={c.coupon_id} className="px-4 py-3 text-sm flex flex-wrap gap-x-3 gap-y-1">
                    {c.kind === 'hall_pass' ? (
                      <span className="font-medium text-status-success">Hall Pass</span>
                    ) : (
                      <span className="font-mono tracking-widest font-medium">{c.code}</span>
                    )}
                    <span>{c.plan_name ?? c.plan_id}</span>
                    <Pill tone="brand">
                      {c.kind === 'hall_pass'
                        ? hallPassLabel(c.free_hours ?? 0)
                        : `${c.free_days ?? 0} días gratis`}
                    </Pill>
                    <span
                      className="text-ink-muted ml-auto"
                      title={absoluteTime(c.redeemed_at)}
                    >
                      {relativeTime(c.redeemed_at)}
                    </span>
                  </li>
                ))}
              </ul>
            </DataCard>
          ) : null}

          {tos_acceptances.length > 0 ? (
            <DataCard
              icon={ScrollText}
              title="Términos aceptados"
              count={tos_acceptances.length}
              helpId="user.terms"
            >
              <ul className="divide-y divide-surface-border dark:divide-surface-dark-border">
                {tos_acceptances.map((t) => (
                  <li
                    key={`${t.partner_id}-${t.tos_version}`}
                    className="px-4 py-2.5 text-sm flex flex-wrap items-center gap-x-3 gap-y-1"
                  >
                    <Pill tone="neutral">{t.partner_id}</Pill>
                    <span className="font-medium">v{t.tos_version}</span>
                    {t.ip ? (
                      <span className="text-xs font-mono text-ink-subtle">desde {t.ip}</span>
                    ) : null}
                    <span className="text-ink-muted ml-auto" title={absoluteTime(t.accepted_at)}>
                      {shortDate(t.accepted_at)}
                    </span>
                  </li>
                ))}
              </ul>
            </DataCard>
          ) : null}
        </div>

        <aside className="min-w-0 lg:sticky lg:top-4" data-help="user.audit">
          <AdminHistoryCard entries={recent_audit} />
        </aside>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tarjeta de cuenta
// ---------------------------------------------------------------------------

function AccountCard({
  user,
  partnerIds,
}: {
  user: UserDetailResponse['user'];
  partnerIds: string[];
}): JSX.Element {
  return (
    <DataCard icon={UserIcon} title="Cuenta">
      <dl className="p-4 grid gap-x-4 gap-y-3 grid-cols-2 md:grid-cols-3">
        <Field label="Email" value={user.email} copyable={user.email ?? false} />
        <Field label="Nombre" value={user.first_name ?? user.display_name} />
        <Field label="Apellido" value={user.last_name} />
        <Field label="Teléfono" value={user.phone} copyable={user.phone ?? false} />
        <Field
          label="Documento"
          value={
            user.document ? (
              <span className="inline-flex items-center gap-1">
                <IdCard size={13} className="shrink-0 text-ink-subtle" aria-hidden="true" />
                {user.document_type ? `${user.document_type} ` : ''}
                {user.document}
              </span>
            ) : (
              <span className="text-ink-subtle">sin registrar</span>
            )
          }
          copyable={user.document ?? false}
          // El dato se captura en el pago, así que puede ser de un tercero. Que
          // esto figure evita que soporte lo use como prueba de identidad.
          hint="Documento declarado por el titular del pago — puede no ser el usuario"
        />
        <Field label="Alta" value={shortDate(user.created_at)} hint={absoluteTime(user.created_at)} />
        <Field
          label="Newsletter"
          value={user.marketing_opt_in ? 'sí, aceptó' : 'no'}
        />
        <Field
          label="Partners"
          value={
            partnerIds.length > 0 ? (
              <span className="flex flex-wrap gap-1">
                {partnerIds.map((p) => (
                  <Pill key={p} tone="neutral">
                    {p}
                  </Pill>
                ))}
              </span>
            ) : (
              '—'
            )
          }
        />
        <Field label="user_id" value={user.user_id} mono copyable={user.user_id} />
      </dl>
    </DataCard>
  );
}

// ---------------------------------------------------------------------------
// Suscripción desplegable
// ---------------------------------------------------------------------------

/**
 * Situacion del cobro de una suscripcion en la ficha. El motivo del rechazo
 * sale del ultimo pago fallido; una expirada solo cuenta como "baja por falta
 * de pago" si su ultimo pago fue un rechazo (no si la dio de baja otra cosa).
 */
/**
 * Aviso de una sub `pending`: el alta se inicio pero ningun pago se acredito.
 * Si hubo intentos rechazados, el ultimo con su motivo.
 */
function pendingCheckoutNotice(payments: AdminUserPayment[]): { title: string; detail: string | null } {
  const failed = payments
    .filter((p) => p.status === 'failed')
    .sort((a, b) => b.created_at.localeCompare(a.created_at));
  const last = failed[0];
  if (!last) {
    return {
      title: 'Inició el alta pero no hay ningún pago acreditado: no tiene el plan',
      detail: 'Lo más común es que haya cerrado la pantalla de pago sin completarla.',
    };
  }
  const reason = declineLabel(last.failure_code) ?? 'sin motivo informado';
  return {
    title: 'El pago del alta fue rechazado: no tiene el plan',
    detail:
      `Motivo: ${reason}. ` +
      (failed.length > 1
        ? `${failed.length} intentos rechazados, el último ${relativeTime(last.created_at)}.`
        : `Fue ${relativeTime(last.created_at)}.`),
  };
}

function subChargeSituation(sub: AdminUserSubscription, payments: AdminUserPayment[]): ChargeSituation | null {
  const sorted = [...payments].sort((a, b) => b.created_at.localeCompare(a.created_at));
  const lastFailed = sorted.find((p) => p.status === 'failed') ?? null;
  const last = sorted[0] ?? null;
  return chargeSituation({
    ...sub,
    has_token: sub.external_subscription_id !== null,
    last_failure_code:
      sub.status === 'expired'
        ? last?.status === 'failed'
          ? (last.failure_code ?? 'card_declined')
          : null
        : (lastFailed?.failure_code ?? null),
  });
}

function SubscriptionRow({
  sub,
  payments,
  invoiceByPayment,
  defaultOpen,
  customer,
}: {
  sub: AdminUserSubscription;
  payments: AdminUserPayment[];
  invoiceByPayment: Map<string, AdminUserInvoice>;
  defaultOpen: boolean;
  customer: string | null;
}): JSX.Element {
  const remaining = daysUntil(sub.current_period_end);
  const session = useSession();
  const situation = subChargeSituation(sub, payments);
  const pendingNotice = sub.status === 'pending' ? pendingCheckoutNotice(payments) : null;

  return (
    <ExpandableRow
      defaultOpen={defaultOpen || situation !== null || pendingNotice !== null}
      summary={
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="font-medium truncate">
            {sub.plan_name ?? sub.plan_code ?? 'plan desconocido'}
          </span>
          <Pill tone={SUB_TONE[sub.status]}>{SUB_LABEL[sub.status]}</Pill>
          {situation ? (
            <Pill tone={situation.tone === 'neutral' ? 'warning' : situation.tone} title={situation.title}>
              cobro pendiente
            </Pill>
          ) : null}
          <Pill tone="neutral">{sub.partner_id}</Pill>
          {sub.price_amount ? (
            <span className="text-sm text-ink-muted">
              {money(sub.price_amount, sub.price_currency ?? 'ARS')}
              {sub.billing_period === 'monthly'
                ? ' /mes'
                : sub.billing_period === 'yearly'
                  ? ' /año'
                  : sub.billing_period === 'weekly'
                    ? ' /semana'
                    : ''}
            </span>
          ) : null}
          <span
            className="text-sm text-ink-muted ml-auto"
            title={absoluteTime(sub.current_period_end)}
          >
            {sub.current_period_end
              ? remaining !== null && remaining < 0
                ? `venció ${relativeTime(sub.current_period_end)}`
                : `vence ${relativeTime(sub.current_period_end)}`
              : sub.status === 'pending'
                ? 'no tiene el plan'
                : 'sin vencimiento'}
          </span>
        </div>
      }
    >
      {pendingNotice ? (
        <div
          data-help="user.pending"
          className="my-3 rounded-lg border p-3 space-y-1 border-status-warning/40 bg-status-warning/5"
        >
          <p className="text-sm font-medium text-status-warning">{pendingNotice.title}</p>
          {pendingNotice.detail ? (
            <p className="text-xs text-ink-muted break-words">{pendingNotice.detail}</p>
          ) : null}
          <p className="text-xs text-ink-muted">
            El cliente puede volver a intentarlo desde la app. Si asegura que se le cobró, usá
            «Reconciliar pago»: consulta a Line y activa el plan solo si el pago se acreditó.
          </p>
        </div>
      ) : null}
      {situation ? (
        <div
          data-help="user.charge"
          className={`my-3 rounded-lg border p-3 flex flex-col md:flex-row md:items-start gap-3 ${
            situation.tone === 'error'
              ? 'border-status-error/40 bg-status-error/5'
              : situation.tone === 'warning'
                ? 'border-status-warning/40 bg-status-warning/5'
                : 'border-surface-border dark:border-surface-dark-border bg-ink-muted/5'
          }`}
        >
          <div className="flex-1 space-y-1">
            <p
              className={`text-sm font-medium ${
                situation.tone === 'error'
                  ? 'text-status-error'
                  : situation.tone === 'warning'
                    ? 'text-status-warning'
                    : ''
              }`}
            >
              {situation.title}
            </p>
            {situation.detail ? (
              <p className="text-xs text-ink-muted break-words">{situation.detail}</p>
            ) : null}
            <p className="text-xs text-ink-muted">{situation.next}</p>
          </div>
          <div className="md:w-72 shrink-0">
            <ChargeRetryButton
              disabled={!canWrite(session, 'users')}
              disabledReason="Requiere Usuarios en nivel completo"
              target={{
                subscriptionId: sub.subscription_id,
                status: sub.status,
                customer,
                planName: sub.plan_name,
                amount: sub.price_amount,
                currency: sub.price_currency,
              }}
            />
          </div>
        </div>
      ) : null}

      <dl className="grid gap-x-4 gap-y-3 grid-cols-2 md:grid-cols-4 py-3">
        <Field label="Alta" value={shortDate(sub.started_at)} hint={absoluteTime(sub.started_at)} />
        <Field
          label="Período actual"
          value={
            sub.current_period_start
              ? `${shortDate(sub.current_period_start)} → ${shortDate(sub.current_period_end)}`
              : shortDate(sub.current_period_end)
          }
        />
        <Field
          label="Renovación automática"
          value={sub.auto_renew ? 'activada' : 'desactivada'}
        />
        <Field label="Medio de pago" value={sub.payment_provider} />
        {sub.grace_period_end ? (
          <Field
            label="Gracia hasta"
            value={shortDate(sub.grace_period_end)}
            hint={absoluteTime(sub.grace_period_end)}
          />
        ) : null}
        {sub.verification_grace_until ? (
          <Field
            label="Gracia de verificación"
            value={absoluteTime(sub.verification_grace_until)}
            hint="Hasta cuándo puede entrar sin verificar el email (migración 0037)"
          />
        ) : null}
        {sub.cancelled_at ? (
          <Field label="Cancelada" value={shortDate(sub.cancelled_at)} />
        ) : null}
        <Field label="subscription_id" value={sub.subscription_id} mono copyable={sub.subscription_id} />
        {sub.external_subscription_id ? (
          <Field
            label="ID en la pasarela"
            value={sub.external_subscription_id}
            mono
            copyable={sub.external_subscription_id}
          />
        ) : null}
      </dl>

      <h3 className="text-xs uppercase tracking-wide text-ink-subtle mt-2 mb-1">
        Pagos de esta suscripción
      </h3>
      {payments.length === 0 ? (
        <p className="text-sm text-ink-muted py-2">Sin movimientos registrados.</p>
      ) : (
        <ul className="divide-y divide-surface-border dark:divide-surface-dark-border">
          {payments.map((p) => {
            const invoice = invoiceByPayment.get(p.payment_id);
            return (
              <li key={p.payment_id} className="py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                <span className="tabular-nums text-ink-muted w-24" title={absoluteTime(p.created_at)}>
                  {shortDate(p.created_at)}
                </span>
                <span className="font-medium tabular-nums">{money(p.amount, p.currency)}</span>
                <Pill tone={PAYMENT_TONE[p.status]}>{PAYMENT_LABEL[p.status]}</Pill>
                {p.failure_code ? (
                  <span className="text-xs text-status-error" title={p.failure_code}>
                    {declineLabel(p.failure_code)}
                  </span>
                ) : null}
                <span className="ml-auto flex items-center gap-2">
                  {invoice ? (
                    invoice.pdf_url ? (
                      <a
                        href={invoice.pdf_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-brand hover:underline"
                        title={`CAE ${invoice.cae ?? '—'}`}
                      >
                        <FileText size={13} aria-hidden="true" />
                        {invoice.full_number ?? 'factura'}
                      </a>
                    ) : (
                      <Pill tone={INVOICE_TONE[invoice.status]}>factura {invoice.status}</Pill>
                    )
                  ) : p.status === 'succeeded' ? (
                    <span className="text-xs text-ink-subtle">sin factura</span>
                  ) : null}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </ExpandableRow>
  );
}

// ---------------------------------------------------------------------------
// Intentos de pago y resultado de «Reconciliar pago»
// ---------------------------------------------------------------------------

const TONE_TEXT: Record<ReconcileTone, string> = {
  success: 'text-status-success',
  warning: 'text-status-warning',
  error: 'text-status-error',
  critical: 'text-status-error',
  neutral: 'text-ink-muted dark:text-ink-inverted-muted',
};

/** Id de pago abreviado, con el completo al pasar el mouse y al copiar. */
function PaymentId({ id }: { id: string }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-0.5 font-mono text-xs" title={id}>
      {shortPaymentId(id)}
      <CopyButton value={id} label="ID de pago" />
    </span>
  );
}

/** Cuantos intentos se ven sin apretar «ver todos». */
const ATTEMPTS_COLLAPSED = 5;

/**
 * Cada vez que se abrió el formulario de pago queda un intento. Se muestran
 * siempre, sin apretar nada: es lo que explica por qué alguien "pagó" y no
 * tiene el plan (pagó con otro intento, lo rechazaron, o nunca completó).
 */
function AttemptsCard({
  attempts,
  userId,
  customer,
}: {
  attempts: CheckoutAttempt[];
  userId: string;
  customer: string | null;
}): JSX.Element {
  const [showAll, setShowAll] = useState(false);
  const duplicates = duplicateAttempts(attempts);
  // Un duplicado nunca queda escondido detrás de «ver todos».
  const shown = showAll
    ? attempts
    : attempts.filter((a, i) => i < ATTEMPTS_COLLAPSED || a.status === 'duplicate');
  const hidden = attempts.length - shown.length;

  return (
    <DataCard icon={ListChecks} title="Intentos de pago" count={attempts.length} helpId="user.attempts">
      {duplicates.length > 0 ? (
        <div
          role="alert"
          data-help="user.attempts.duplicate"
          className="px-4 py-3 text-sm border-b border-status-error/30 bg-status-error/10 text-status-error flex items-start gap-2"
        >
          <AlertTriangle size={16} className="shrink-0 mt-0.5" aria-hidden="true" />
          <span>
            <strong>
              {duplicates.length === 1
                ? 'Hay un cobro duplicado a reintegrar.'
                : `Hay ${duplicates.length} cobros duplicados a reintegrar.`}
            </strong>{' '}
            La pasarela aprobó un pago cuando la suscripción ya estaba paga con otro. Copiá el ID
            del pago, pedí el reintegro y, cuando esté hecho, marcalo como reintegrado.
          </span>
        </div>
      ) : null}
      <ul className="divide-y divide-surface-border dark:divide-surface-dark-border">
        {shown.map((a) => {
          const view = ATTEMPT_VIEW[a.status];
          const line = lineStatusLabel(a.provider_status);
          const reason = declineLabel(a.reason);
          return (
            <li
              key={a.payment_id}
              className={`px-4 py-2.5 text-sm flex flex-wrap items-center gap-x-3 gap-y-1 ${
                a.status === 'duplicate' ? 'bg-status-error/5' : ''
              }`}
            >
              <Pill tone={view.tone}>{view.label}</Pill>
              <span className="font-medium tabular-nums">{money(a.amount, a.currency)}</span>
              <span className="text-ink-muted tabular-nums" title={absoluteTime(a.created_at)}>
                {shortDate(a.created_at)} · {relativeTime(a.created_at)}
              </span>
              <PaymentId id={a.payment_id} />
              {a.source === 'manual' ? (
                <Pill tone="neutral" title="Lo ingresó un operador con el ID de pago de Line">
                  ingresado a mano
                </Pill>
              ) : null}
              {a.status === 'duplicate' ? (
                <span className="ml-auto">
                  <MarkRefundedButton
                    userId={userId}
                    paymentId={a.payment_id}
                    amount={a.amount}
                    currency={a.currency}
                    customer={customer}
                  />
                </span>
              ) : null}
              {a.status === 'refunded' ? (
                <span
                  data-help="user.attempts.refunded"
                  className="basis-full text-xs text-ink-muted break-words"
                  title={a.refunded_at ? absoluteTime(a.refunded_at) : undefined}
                >
                  Reintegrado
                  {a.refunded_at ? ` el ${shortDate(a.refunded_at)}` : ''}
                  {a.refund_reference ? ` · Referencia: ${a.refund_reference}` : ''}
                </span>
              ) : null}
              {reason || line ? (
                <span
                  className={`basis-full text-xs break-words ${
                    a.status === 'rejected' || a.status === 'duplicate'
                      ? 'text-status-error'
                      : 'text-ink-muted'
                  }`}
                  title={
                    a.checked_at
                      ? `Última consulta a Line: ${absoluteTime(a.checked_at)}`
                      : 'Todavía no se consultó a Line por este intento'
                  }
                >
                  {[reason ? `Motivo: ${reason}` : null, line ? `Line: ${line}` : null]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>
      {attempts.length > ATTEMPTS_COLLAPSED ? (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          aria-expanded={showAll}
          className="w-full px-4 py-2 text-xs text-brand text-left border-t border-surface-border dark:border-surface-dark-border hover:bg-surface-muted dark:hover:bg-surface-dark-muted focus-visible:ring-2 focus-visible:ring-brand-light"
        >
          {showAll ? 'ver menos' : `ver todos (${hidden} más)`}
        </button>
      ) : null}
    </DataCard>
  );
}

/**
 * Lo que devolvió «Reconciliar pago», pago por pago. Se muestra SIEMPRE que se
 * apretó el botón: qué se consultó, qué dijo Line y en qué quedó. Un renglón
 * genérico no distingue "no pagó" de "lo rechazaron" de "pagó dos veces".
 */
function ReconcileResults({
  res,
  onClose,
}: {
  res: ReconcileResult;
  onClose: () => void;
}): JSX.Element {
  const summary = reconcileSummary(res);
  const th = 'px-3 py-2 text-left text-[11px] uppercase tracking-wide font-normal text-ink-subtle';

  return (
    <section
      data-help="user.actions.reconcile.results"
      aria-label="Resultado de reconciliar el pago"
      className="bg-surface dark:bg-surface-dark text-ink dark:text-ink-inverted rounded-xl border border-surface-border dark:border-surface-dark-border overflow-hidden"
    >
      <header className="px-4 py-3 flex items-start gap-2">
        <p role="status" className={`flex-1 text-sm font-medium flex items-start gap-2 ${TONE_TEXT[summary.tone]}`}>
          {summary.tone === 'success' ? (
            <Check size={16} className="shrink-0 mt-0.5" aria-hidden="true" />
          ) : summary.tone === 'neutral' ? (
            <Clock size={16} className="shrink-0 mt-0.5" aria-hidden="true" />
          ) : (
            <AlertTriangle size={16} className="shrink-0 mt-0.5" aria-hidden="true" />
          )}
          {summary.text}
        </p>
        <button
          type="button"
          onClick={onClose}
          title="Cerrar el resultado"
          aria-label="Cerrar el resultado"
          className="shrink-0 p-1 rounded text-ink-subtle hover:text-ink dark:hover:text-ink-inverted focus-visible:ring-2 focus-visible:ring-brand-light"
        >
          <X size={14} aria-hidden="true" />
        </button>
      </header>
      {res.results.length > 0 ? (
        <div className="overflow-x-auto border-t border-surface-border dark:border-surface-dark-border">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th scope="col" className={th}>Pago</th>
                <th scope="col" className={th}>Fecha del intento</th>
                <th scope="col" className={th}>Qué dijo Line</th>
                <th scope="col" className={th}>Resultado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-border dark:divide-surface-dark-border">
              {res.results.map((r) => {
                const view = OUTCOME_VIEW[r.outcome];
                const attempt = attemptFor(res.attempts, r.payment_id);
                const line = lineStatusLabel(r.line_status);
                return (
                  <tr
                    key={`${r.subscription_id}-${r.payment_id}`}
                    className={`align-top ${r.outcome === 'duplicate' ? 'bg-status-error/5' : ''}`}
                  >
                    <td className="px-3 py-2 whitespace-nowrap">
                      <PaymentId id={r.payment_id} />
                    </td>
                    <td
                      className="px-3 py-2 whitespace-nowrap tabular-nums text-ink-muted"
                      title={attempt ? relativeTime(attempt.created_at) : 'Sin intento registrado'}
                    >
                      {attempt ? absoluteTime(attempt.created_at) : '—'}
                    </td>
                    <td className="px-3 py-2">
                      {line ? (
                        <span title={`Estado en Line: ${r.line_status ?? ''}`}>{line}</span>
                      ) : (
                        <span className="text-ink-subtle">sin respuesta</span>
                      )}
                    </td>
                    <td className="px-3 py-2 min-w-[16rem]">
                      <Pill tone={view.tone}>{view.label}</Pill>
                      <p className="mt-1 text-xs text-ink-muted break-words">{outcomeDetail(r)}</p>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Dispositivos
// ---------------------------------------------------------------------------

function DevicesCard({
  devices,
  userId,
}: {
  devices: AdminDeviceSummary[];
  userId: string;
}): JSX.Element {
  const session = useSession();
  const canAct = canWrite(session, 'users');
  const qc = useQueryClient();
  const [target, setTarget] = useState<AdminDeviceSummary | null>(null);

  const remove = useMutation({
    mutationFn: (d: AdminDeviceSummary) => adminDeleteUserDevice(userId, d.device_id, d.partner_id),
    onSuccess: () => {
      setTarget(null);
      qc.invalidateQueries({ queryKey: ['admin-user-detail'] });
    },
  });

  return (
    <>
      <DataCard icon={Monitor} title="Dispositivos activos" count={devices.length}>
        {devices.length === 0 ? (
          <EmptyNote>
            Sin dispositivos registrados. Aparecen acá cuando el usuario inicia sesión.
          </EmptyNote>
        ) : (
          <ul className="divide-y divide-surface-border dark:divide-surface-dark-border">
            {devices.map((d) => {
              const Icon = DEVICE_ICON[d.device_type] ?? Laptop;
              return (
                <li key={`${d.partner_id}-${d.device_id}`} className="px-4 py-3 flex gap-3">
                  <Icon size={20} className="shrink-0 mt-0.5 text-ink-subtle" aria-hidden="true" />
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="font-medium text-sm truncate">
                        {d.device_model ?? DEVICE_LABEL[d.device_type] ?? d.device_type}
                      </span>
                      <Pill tone="neutral">{DEVICE_LABEL[d.device_type] ?? d.device_type}</Pill>
                      <Pill tone="neutral">{d.partner_id}</Pill>
                    </div>
                    <p className="text-xs text-ink-muted truncate">
                      {[d.os, d.os_version, d.browser].filter(Boolean).join(' · ') || 'sin detalle'}
                    </p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-muted">
                      {d.geo_region ? (
                        <span className="inline-flex items-center gap-1">
                          <MapPin size={11} aria-hidden="true" />
                          {d.geo_region}
                        </span>
                      ) : null}
                      {d.ip ? (
                        <span className="inline-flex items-center gap-1 font-mono">
                          <Globe size={11} aria-hidden="true" />
                          {d.ip}
                        </span>
                      ) : null}
                      <span title={absoluteTime(d.login_at)}>
                        entró {relativeTime(d.login_at)}
                      </span>
                      <span title={absoluteTime(d.last_seen_at)}>
                        · visto {relativeTime(d.last_seen_at)}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    disabled={!canAct}
                    onClick={() => setTarget(d)}
                    title="Cerrar la sesión de este dispositivo"
                    className="shrink-0 self-start inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-status-warning hover:bg-status-warning/15 disabled:opacity-40 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-brand-light"
                  >
                    <LogOut size={14} aria-hidden="true" />
                    Desloguear
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </DataCard>

      <ConfirmDialog
        open={target !== null}
        title="Cerrar este dispositivo"
        description={
          <div className="space-y-2 text-sm">
            <p>
              Vas a revocar la sesión de{' '}
              <strong>
                {target?.device_model ??
                  (target ? (DEVICE_LABEL[target.device_type] ?? target.device_type) : '')}
              </strong>{' '}
              en <strong>{target?.partner_id}</strong>. Los demás dispositivos del usuario siguen
              conectados.
            </p>
            {remove.isError ? (
              <p className="text-status-error text-xs">
                {remove.error instanceof AdminApiError ? remove.error.message : 'Error'}
              </p>
            ) : null}
          </div>
        }
        confirmLabel="Cerrar dispositivo"
        danger
        loading={remove.isPending}
        onConfirm={() => {
          if (target) remove.mutate(target);
        }}
        onCancel={() => {
          setTarget(null);
          remove.reset();
        }}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Historial administrativo (panel derecho)
// ---------------------------------------------------------------------------

const AUDIT_LABEL: Record<string, string> = {
  'admin.user.block': 'Cuenta bloqueada',
  'admin.user.unblock': 'Cuenta desbloqueada',
  'admin.user.logout_all': 'Todas las sesiones cerradas',
  'admin.user.device_logout': 'Dispositivo deslogueado',
  'admin.user.hall_pass': 'Hall Pass otorgado',
  'admin.subscription.reconcile': 'Pago reconciliado',
  'checkout_attempt.marked_refunded': 'Cobro duplicado marcado como reintegrado',
  'user.password_changed': 'Cambió su contraseña',
  'coupon.redeem': 'Canjeó un cupón',
  'tos.accepted': 'Aceptó los términos',
};

function AdminHistoryCard({
  entries,
}: {
  entries: UserDetailResponse['recent_audit'];
}): JSX.Element {
  return (
    <DataCard icon={History} title="Historial de la cuenta" count={entries.length}>
      {entries.length === 0 ? (
        <EmptyNote>Nadie tocó esta cuenta todavía.</EmptyNote>
      ) : (
        <ol className="max-h-[calc(100vh-12rem)] overflow-auto divide-y divide-surface-border dark:divide-surface-dark-border">
          {entries.map((a) => (
            <li key={a.audit_id} className="px-4 py-3 text-sm">
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-medium">{AUDIT_LABEL[a.action] ?? a.action}</span>
                <span
                  className="text-xs text-ink-subtle shrink-0"
                  title={absoluteTime(a.occurred_at)}
                >
                  {relativeTime(a.occurred_at)}
                </span>
              </div>
              <p className="text-xs text-ink-muted truncate" title={`${a.actor_type}:${a.actor_id}`}>
                {a.actor_type === 'admin_user'
                  ? 'por un admin'
                  : a.actor_type === 'self'
                    ? 'por el propio usuario'
                    : a.actor_type === 'system'
                      ? 'automático'
                      : a.actor_type}
              </p>
              {a.reason ? (
                <p className="mt-1 text-xs text-ink-muted italic break-words">«{a.reason}»</p>
              ) : null}
            </li>
          ))}
        </ol>
      )}
    </DataCard>
  );
}

// ---------------------------------------------------------------------------
// Acciones
// ---------------------------------------------------------------------------

type UserAction = 'logout-all' | 'block' | 'unblock' | 'hall-pass';

/** Renglón de resultado del Hall Pass: dice qué pasó con el plan, no solo "ok". */
function hallPassMessage(res: HallPassResult): { kind: 'ok' | 'info'; text: string } {
  const dur = hallPassLabel(res.hours);
  const until = absoluteTime(res.access_until ?? res.grace_until);
  switch (res.plan_outcome) {
    case 'upgraded':
      return {
        kind: 'ok',
        text: `Hall Pass de ${dur}: plan ${res.plan_name ?? res.plan_id} y acceso sin verificar el email (hasta ${until}). Después pasa al plan gratuito.`,
      };
    case 'renewed':
      return {
        kind: 'ok',
        text: `Hall Pass renovado por ${dur} desde ahora: plan ${res.plan_name ?? res.plan_id} (hasta ${until}).`,
      };
    case 'kept_paid_plan':
      return {
        kind: 'ok',
        text: `Ya tiene un plan pago (${res.plan_name ?? res.plan_id}), así que no se le tocó. Se lo eximió de verificar el email por ${dur} (hasta ${until}).`,
      };
    case 'no_paid_plan':
    default:
      // La exención sobre una sub pending queda escrita pero el gate del login
      // solo mira subs vivas: decirlo evita que soporte crea que destrabó algo.
      return res.effective
        ? {
            kind: 'info',
            text: `${res.partner_id} no tiene ningún plan pago activo para dar. Solo se lo eximió de verificar el email por ${dur} (hasta ${until}).`,
          }
        : {
            kind: 'info',
            text: `${res.partner_id} no tiene ningún plan pago activo para dar, y la exención todavía no habilita el login: la suscripción está ${res.subscription_status}.`,
          };
  }
}

function UserAdminActions({
  user,
  userId,
}: {
  user: { status: 'active' | 'blocked' | 'deactivated'; email_verified: boolean };
  userId: string;
}): JSX.Element {
  const session = useSession();
  const qc = useQueryClient();
  const canAct = canWrite(session, 'users');

  const [action, setAction] = useState<UserAction | null>(null);
  const [reason, setReason] = useState('');
  const [emailReportOpen, setEmailReportOpen] = useState(false);
  // Duración elegida en el menú del Hall Pass; el diálogo de confirmación la usa.
  const [hallPassHours, setHallPassHours] = useState<number | null>(null);
  const [hallPassMenu, setHallPassMenu] = useState(false);
  const hallPassMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hallPassMenu) return;
    function onDocClick(e: MouseEvent): void {
      if (hallPassMenuRef.current && !hallPassMenuRef.current.contains(e.target as Node)) {
        setHallPassMenu(false);
      }
    }
    function onEsc(e: KeyboardEvent): void {
      if (e.key === 'Escape') setHallPassMenu(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [hallPassMenu]);

  const invalidate = (): void => {
    setAction(null);
    setReason('');
    void qc.invalidateQueries({ queryKey: ['admin-user-detail'] });
  };

  const logoutAll = useMutation({
    mutationFn: (r: string) => adminLogoutAll(userId, r || undefined),
    onSuccess: invalidate,
  });
  const block = useMutation({
    mutationFn: (r: string) => adminBlockUser(userId, r),
    onSuccess: invalidate,
  });
  const unblock = useMutation({
    mutationFn: (r: string) => adminUnblockUser(userId, r || undefined),
    onSuccess: invalidate,
  });

  // Renglón de resultado compartido por las acciones que no cambian nada
  // visible de por sí (reconciliar, hall pass): sin él, el operador aprieta el
  // botón y no sabe si pasó algo.
  const [reconcileMsg, setReconcileMsg] = useState<
    { kind: 'ok' | 'info' | 'error'; text: string } | null
  >(null);

  const hallPass = useMutation({
    mutationFn: (r: string) => adminGrantHallPass(userId, hallPassHours ?? 0, r || undefined),
    onSuccess: (res: HallPassResult) => {
      invalidate();
      setReconcileMsg(hallPassMessage(res));
    },
    onError: (e: unknown) => {
      setReconcileMsg({
        kind: 'error',
        text: e instanceof AdminApiError ? e.message : 'No se pudo otorgar el Hall Pass.',
      });
    },
  });

  // Resultado de «Reconciliar pago», pago por pago. Va aparte del renglón de
  // arriba porque es una tabla, y queda en pantalla hasta que se cierra.
  const [reconcileRes, setReconcileRes] = useState<ReconcileResult | null>(null);
  // ID de pago de Line ingresado a mano (opcional): para el pago que el cliente
  // o Line informan y no figura entre los intentos de la cuenta.
  const [manualPaymentId, setManualPaymentId] = useState('');
  const manualId = normalizeLinePaymentId(manualPaymentId);
  const manualIdInvalid = manualId !== '' && !isLinePaymentId(manualId);

  const reconcile = useMutation({
    mutationFn: (paymentId: string) =>
      adminReconcileSubscription(userId, paymentId ? { paymentId } : {}),
    onSuccess: (res: ReconcileResult, paymentId: string) => {
      setReconcileRes(res);
      // El ID se limpia solo si ya quedó resuelto; si no, queda para corregirlo.
      const settled = res.results.some(
        (r) =>
          r.payment_id === paymentId &&
          (r.outcome === 'activated' || r.outcome === 'already_active' || r.outcome === 'duplicate'),
      );
      if (settled) setManualPaymentId('');
      // Siempre: la consulta actualiza los intentos aunque no active nada.
      void qc.invalidateQueries({ queryKey: ['admin-user-detail'] });
    },
    onError: (e: unknown) => {
      setReconcileMsg({
        kind: 'error',
        text: e instanceof AdminApiError ? e.message : 'Error al reconciliar.',
      });
    },
  });

  const mut =
    action === 'block'
      ? block
      : action === 'unblock'
        ? unblock
        : action === 'hall-pass'
          ? hallPass
          : logoutAll;

  const hallPassText = hallPassLabel(hallPassHours ?? 0);

  const btn =
    'min-h-[2.25rem] inline-flex items-center gap-2 rounded-lg px-3 text-sm disabled:opacity-50 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-brand-light';

  return (
    <>
      <section
        data-help="user.actions"
        className="bg-surface dark:bg-surface-dark rounded-xl border border-surface-border dark:border-surface-dark-border p-3 flex flex-wrap gap-2"
      >
        <button
          type="button"
          disabled={!canAct}
          onClick={() => setAction('logout-all')}
          data-help="user.actions.logout-all"
          className={`${btn} bg-status-warning/15 text-status-warning hover:bg-status-warning/25`}
        >
          <Lock size={16} className="shrink-0" aria-hidden="true" />
          Cerrar todas las sesiones
        </button>
        {user.status === 'active' ? (
          <button
            type="button"
            disabled={!canAct}
            onClick={() => setAction('block')}
            data-help="user.actions.block"
            className={`${btn} bg-status-error/15 text-status-error hover:bg-status-error/25`}
          >
            <Ban size={16} className="shrink-0" aria-hidden="true" />
            Bloquear
          </button>
        ) : (
          <button
            type="button"
            disabled={!canAct}
            onClick={() => setAction('unblock')}
            data-help="user.actions.unblock"
            className={`${btn} bg-status-success/15 text-status-success hover:bg-status-success/25`}
          >
            <LockOpen size={16} className="shrink-0" aria-hidden="true" />
            Desbloquear
          </button>
        )}
        <div ref={hallPassMenuRef} className="relative">
          <button
            type="button"
            disabled={!canAct || hallPass.isPending}
            onClick={() => setHallPassMenu((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={hallPassMenu}
            data-help="user.actions.hall-pass"
            title="Por el tiempo que elijas: acceso sin verificar el email y el plan más caro del partner"
            className={`${btn} bg-status-success/15 text-status-success hover:bg-status-success/25`}
          >
            <Ticket size={16} className="shrink-0" aria-hidden="true" />
            Hall Pass
            <ChevronDown size={14} className="shrink-0" aria-hidden="true" />
          </button>
          {hallPassMenu ? (
            <div
              role="menu"
              aria-label="Duración del Hall Pass"
              className="absolute left-0 mt-1 w-40 z-30 overflow-hidden rounded-lg border border-surface-border dark:border-surface-dark-border bg-surface dark:bg-surface-dark shadow-lg"
            >
              {HALL_PASS_OPTIONS.map((o) => (
                <button
                  key={o.hours}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setHallPassMenu(false);
                    setHallPassHours(o.hours);
                    setReconcileMsg(null);
                    setAction('hall-pass');
                  }}
                  className="block w-full px-4 py-2 text-left text-sm text-ink dark:text-ink-inverted hover:bg-surface-muted dark:hover:bg-surface-dark-muted"
                >
                  {o.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => setEmailReportOpen(true)}
          data-help="user.actions.email-report"
          title="Investiga la entregabilidad de los mails de verificación / reset (últimos 7 días)"
          className={`${btn} bg-status-info/15 text-status-info hover:bg-status-info/25`}
        >
          <MailSearch size={16} className="shrink-0" aria-hidden="true" />
          Investigar correos
        </button>
        {!canAct ? (
          <span className="text-xs text-ink-subtle self-center">
            Tu rol es de solo lectura.
          </span>
        ) : null}

        {/* Reconciliar va en su propio renglón: es la única acción con un dato
            de entrada, y el campo tiene que quedar pegado al botón que lo usa. */}
        <form
          noValidate
          className="basis-full flex flex-wrap items-end gap-2 pt-3 mt-1 border-t border-surface-border dark:border-surface-dark-border"
          onSubmit={(e) => {
            e.preventDefault();
            if (!canAct || reconcile.isPending || manualIdInvalid) return;
            setReconcileMsg(null);
            setReconcileRes(null);
            reconcile.mutate(manualId);
          }}
        >
          <label className="block flex-1 min-w-[16rem] max-w-md text-xs text-ink dark:text-ink-inverted">
            <span className="font-medium">ID de pago de Line</span>{' '}
            <span className="text-ink-subtle">(opcional)</span>
            <input
              type="text"
              value={manualPaymentId}
              onChange={(e) => setManualPaymentId(e.target.value)}
              disabled={!canAct || reconcile.isPending}
              placeholder="pay_xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              autoComplete="off"
              spellCheck={false}
              aria-invalid={manualIdInvalid}
              aria-describedby="reconcile-payment-id-hint"
              data-help="user.actions.reconcile.payment-id"
              className={fieldCls({ mono: true, invalid: manualIdInvalid, extra: 'mt-1' })}
            />
          </label>
          <button
            type="submit"
            disabled={!canAct || reconcile.isPending || manualIdInvalid}
            data-help="user.actions.reconcile"
            title="Consulta a Line por los intentos de pago de esta cuenta y activa el plan si alguno está aprobado"
            className={`${btn} min-h-[2.5rem] bg-status-info/15 text-status-info hover:bg-status-info/25`}
          >
            <RefreshCw
              size={16}
              className={`shrink-0 ${reconcile.isPending ? 'animate-spin' : ''}`}
              aria-hidden="true"
            />
            {reconcile.isPending ? 'Consultando a Line…' : 'Reconciliar pago'}
          </button>
          <p
            id="reconcile-payment-id-hint"
            className={`basis-full text-xs ${manualIdInvalid ? 'text-status-error' : 'text-ink-muted dark:text-ink-inverted-muted'}`}
          >
            {manualIdInvalid
              ? 'Ese no es un ID de pago de Line: empieza con «pay_» y sigue con 36 caracteres (pay_xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx).'
              : 'Dejalo vacío para consultar los intentos de esta cuenta. Completalo solo si el cliente o Line informan un pago que no figura: el ID sale del panel de Line, en el detalle del pago.'}
          </p>
        </form>
      </section>

      {reconcileMsg ? (
        <p
          className={`text-sm px-1 flex items-center gap-2 ${
            reconcileMsg.kind === 'ok'
              ? 'text-status-success'
              : reconcileMsg.kind === 'error'
                ? 'text-status-error'
                : 'text-ink-muted'
          }`}
        >
          {reconcileMsg.kind === 'ok' ? (
            <Check size={14} aria-hidden="true" />
          ) : (
            <Clock size={14} aria-hidden="true" />
          )}
          {reconcileMsg.text}
        </p>
      ) : null}

      {reconcileRes ? (
        <ReconcileResults res={reconcileRes} onClose={() => setReconcileRes(null)} />
      ) : null}

      <EmailReportModal
        userId={userId}
        open={emailReportOpen}
        onClose={() => setEmailReportOpen(false)}
      />

      <ConfirmDialog
        open={action !== null}
        title={
          action === 'logout-all'
            ? 'Cerrar todas las sesiones'
            : action === 'block'
              ? 'Bloquear cuenta'
              : action === 'hall-pass'
                ? `Otorgar un Hall Pass de ${hallPassText}`
                : 'Desbloquear cuenta'
        }
        description={
          <div className="space-y-2">
            <p>
              {action === 'logout-all'
                ? 'Vas a revocar todas las sesiones de este usuario. Va a tener que loguearse de nuevo en todos sus dispositivos.'
                : action === 'block'
                  ? 'Vas a impedir el login y revocar todas las sesiones. El usuario no va a poder acceder hasta que lo desbloquees.'
                  : action === 'hall-pass'
                    ? `Durante ${hallPassText}, contados desde ahora, va a poder entrar sin verificar el email y va a tener el plan pago más caro del partner, sin cobro. Al terminar pasa al plan gratuito. Si ya tiene un plan pago, no se le toca: solo se lo exime de verificar. Reemplaza el Hall Pass o la ventana de gracia que tuviera. Si ya se le cortó la sesión, tiene que volver a loguearse.`
                    : 'Vas a reactivar la cuenta. El usuario va a poder loguearse de nuevo (las sesiones previas no se restablecen).'}
            </p>
            {action === 'hall-pass' && user.email_verified ? (
              <p className="text-sm text-ink-muted">
                Este usuario ya tiene el email verificado: la exención no le cambia nada. Lo que
                sí recibe es el plan del pase (si no tiene uno pago).
              </p>
            ) : null}
            <label className="block text-sm">
              <span className="font-medium">
                Motivo {action === 'block' ? '(requerido)' : '(opcional)'}:
              </span>
              <textarea
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required={action === 'block'}
                className={fieldCls({ extra: 'mt-1' })}
              />
            </label>
            {mut.isError ? (
              <p className="text-status-error text-xs">
                {mut.error instanceof AdminApiError ? mut.error.message : 'Error'}
              </p>
            ) : null}
          </div>
        }
        confirmLabel={
          action === 'logout-all'
            ? 'Revocar sesiones'
            : action === 'block'
              ? 'Bloquear'
              : action === 'hall-pass'
                ? `Dar ${hallPassText}`
                : 'Desbloquear'
        }
        danger={action === 'logout-all' || action === 'block'}
        loading={mut.isPending}
        onConfirm={() => {
          if (action === 'block' && !reason.trim()) return;
          mut.mutate(reason);
        }}
        onCancel={() => {
          setAction(null);
          setReason('');
          mut.reset();
        }}
      />
    </>
  );
}
